import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { useTiers, useUpgradeSubscription } from '@/hooks/useSubscription';
import type { SubscriptionTier } from '@/types/subscription';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier: string;
  newTier?: string;
  onSuccess?: () => void;

  // NEW: Limit context for when user hits limit
  limitContext?: {
    currentCount: number;
    attemptedAdd: number;
  };
}

const tierPrices: Record<string, { monthly: number; yearly: number }> = {
  FREE: { monthly: 0, yearly: 0 },
  BASIC: { monthly: 29, yearly: 278 },
  PROFESSIONAL: { monthly: 99, yearly: 950 },
  ENTERPRISE: { monthly: 299, yearly: 2870 },
};

const tierFeatures: Record<string, string[]> = {
  FREE: ['100 conversions/month', 'Basic support', 'Standard processing'],
  BASIC: [
    '1,000 conversions/month',
    'Email support',
    'Priority processing',
    'Custom mappings',
  ],
  PROFESSIONAL: [
    '10,000 conversions/month',
    'Priority support',
    'Fast processing',
    'Advanced analytics',
    'API access',
  ],
  ENTERPRISE: [
    'Unlimited conversions',
    'Dedicated support',
    'Fastest processing',
    'Custom integrations',
    'SLA guarantee',
  ],
};

export default function UpgradeModal({
  isOpen,
  onClose,
  currentTier,
  newTier,
  onSuccess,
  limitContext,
}: UpgradeModalProps) {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>(
    'monthly'
  );

  const { data: tiers } = useTiers();
  const upgradeMutation = useUpgradeSubscription();

  const tierOrder = ['FREE', 'BASIC', 'PROFESSIONAL', 'ENTERPRISE'];

  // Filter tiers that would allow the attempted add
  const availableTiers = tiers?.filter(t => {
    const isUpgrade =
      tierOrder.indexOf(t.tier) >
      tierOrder.indexOf(currentTier as SubscriptionTier);

    if (!isUpgrade) return false;

    // If limit context, filter to tiers that meet the need
    if (limitContext) {
      const needed = limitContext.currentCount + limitContext.attemptedAdd;
      return t.employee_limit === -1 || t.employee_limit >= needed;
    }

    return true;
  });

  // Use first available tier if no specific tier provided
  const targetTier = newTier || availableTiers?.[0]?.tier;
  const isUpgrade = targetTier
    ? tierOrder.indexOf(targetTier) > tierOrder.indexOf(currentTier)
    : false;

  const currentPrice = tierPrices[currentTier]?.[billingPeriod] || 0;
  const newPrice = targetTier
    ? tierPrices[targetTier]?.[billingPeriod] || 0
    : 0;
  const priceDifference = newPrice - currentPrice;
  const proratedAmount = Math.abs(priceDifference * 0.5); // Simplified proration

  const handleConfirm = async () => {
    if (!targetTier) return;

    try {
      await upgradeMutation.mutateAsync(targetTier as SubscriptionTier);
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center"
                  >
                    {isUpgrade ? (
                      <ArrowUpIcon className="h-5 w-5 mr-2 text-green-600" />
                    ) : (
                      <ArrowDownIcon className="h-5 w-5 mr-2 text-orange-600" />
                    )}
                    {isUpgrade ? 'Upgrade' : 'Change'} Subscription
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4">
                  {/* Limit Context Message */}
                  {limitContext && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-900">
                        You tried to add{' '}
                        <strong>{limitContext.attemptedAdd}</strong> employee
                        {limitContext.attemptedAdd > 1 ? 's' : ''}, but your
                        current <strong>{currentTier}</strong> plan limits you
                        to the employees you already have. Please upgrade to
                        continue.
                      </p>
                    </div>
                  )}

                  {/* Billing Period Toggle */}
                  <div className="flex items-center justify-center mb-6">
                    <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setBillingPeriod('monthly')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          billingPeriod === 'monthly'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Monthly
                      </button>
                      <button
                        onClick={() => setBillingPeriod('yearly')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          billingPeriod === 'yearly'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Yearly (Save 20%)
                      </button>
                    </div>
                  </div>

                  {/* Comparison */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {/* Current Plan */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Current Plan</p>
                      <p className="text-xl font-bold text-gray-900">
                        {currentTier}
                      </p>
                      <p className="text-2xl font-bold text-gray-600 mt-2">
                        ${currentPrice}
                        <span className="text-sm font-normal">
                          /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      </p>
                    </div>

                    {/* New Plan */}
                    <div className="p-4 bg-indigo-50 rounded-lg border-2 border-indigo-600">
                      <p className="text-xs text-indigo-600 mb-1">New Plan</p>
                      <p className="text-xl font-bold text-indigo-900">
                        {targetTier || 'Select a plan'}
                      </p>
                      <p className="text-2xl font-bold text-indigo-600 mt-2">
                        ${newPrice}
                        <span className="text-sm font-normal">
                          /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Price Difference */}
                  {priceDifference !== 0 && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900">
                          {isUpgrade ? 'Additional Cost' : 'Cost Reduction'}
                        </span>
                        <span className="text-lg font-bold text-blue-900">
                          {isUpgrade ? '+' : '-'}${Math.abs(priceDifference)}
                          <span className="text-sm font-normal">
                            /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                          </span>
                        </span>
                      </div>
                      {isUpgrade && (
                        <p className="text-xs text-blue-700 mt-2">
                          Prorated amount for this period: $
                          {proratedAmount.toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* New Features */}
                  {targetTier && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">
                        {targetTier} Plan Features
                      </h4>
                      <ul className="space-y-2">
                        {tierFeatures[targetTier]?.map((feature, index) => (
                          <motion.li
                            key={index}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="flex items-start"
                          >
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                            <span className="text-sm text-gray-700">
                              {feature}
                            </span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Payment Method (if upgrade) */}
                  {isUpgrade && priceDifference > 0 && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-700">
                          Payment will be charged to your default payment method
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Terms */}
                  <div className="mb-6">
                    <p className="text-xs text-gray-500">
                      {isUpgrade
                        ? 'By upgrading, you agree to be charged the prorated amount immediately. Your new plan will take effect right away.'
                        : 'Your plan will be changed immediately. You will not be charged any additional fees for this billing period.'}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <button
                    onClick={onClose}
                    disabled={upgradeMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={upgradeMutation.isPending || !targetTier}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {upgradeMutation.isPending
                      ? 'Processing...'
                      : isUpgrade
                        ? `Upgrade to ${targetTier}`
                        : `Switch to ${targetTier}`}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
